import type { QueryClient } from '@tanstack/react-query';
import { listSearch, tablePages, textParam } from './lib/list-search.js';
import { z } from 'zod';
import { DISPATCH_LIST_FILTERS } from '@evogard/contract';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  useNavigate,
} from '@tanstack/react-router';
import { Failure, NotFound } from './components/Failure.js';
import { Layout } from './components/Layout.js';
import { can, contextOptions } from './lib/session.js';
import { PipelineScreen } from './modules/billing/PipelineScreen.js';
import { TiersScreen } from './modules/billing/TiersScreen.js';
import { DeskBoardScreen } from './modules/billing/desk/DeskBoardScreen.js';
import { DeskDashboardScreen } from './modules/billing/desk/DeskDashboardScreen.js';
import { DeskMovesScreen } from './modules/billing/desk/DeskMovesScreen.js';
import { DeskImportScreen } from './modules/billing/desk/DeskImportScreen.js';
import { DeskSettingsScreen } from './modules/billing/desk/DeskSettingsScreen.js';
import { AwardsDataScreen } from './modules/billing/awards/AwardsDataScreen.js';
import { AwardsLinksScreen } from './modules/billing/awards/AwardsLinksScreen.js';
import { AwardsHistoryScreen } from './modules/billing/awards/AwardsHistoryScreen.js';
import { AwardsLogScreen } from './modules/billing/awards/AwardsLogScreen.js';
import { AwardsOperatorScreen } from './modules/billing/awards/AwardsOperatorScreen.js';
import { AwardsScreen } from './modules/billing/awards/AwardsScreen.js';
import { AwardsSettingsScreen } from './modules/billing/awards/AwardsSettingsScreen.js';
import {
  ConsultantsScreen,
  consultantsSearch,
} from './modules/sales/ConsultantsScreen.js';
import { CashBookScreen } from './modules/sales/CashBookScreen.js';
import { CommissionsScreen } from './modules/sales/CommissionsScreen.js';
import { TargetsScreen } from './modules/sales/TargetsScreen.js';
import { RankingScreen } from './modules/sales/RankingScreen.js';
import { TeamPanelScreen } from './modules/sales/TeamPanelScreen.js';
import { AwardTemplatesScreen } from './modules/sales/AwardTemplatesScreen.js';
import { LadderScreen } from './modules/sales/LadderScreen.js';
import { SalesScreen } from './modules/sales/SalesScreen.js';
import { SalesReportScreen } from './modules/sales/SalesReportScreen.js';
import { AwardPortfolioScreen } from './modules/sales/AwardPortfolioScreen.js';
import { SalesHomeScreen } from './modules/sales/SalesHomeScreen.js';
import { WalletScreen } from './modules/sales/WalletScreen.js';
import { CalculatorScreen } from './modules/sales/CalculatorScreen.js';
import { StatementScreen } from './modules/sales/StatementScreen.js';
import { AgendaScreen } from './modules/sales/AgendaScreen.js';
import { AgendaMemberScreen } from './modules/sales/AgendaMemberScreen.js';
import { CategoriesScreen } from './modules/tickets/CategoriesScreen.js';
import { TicketScreen } from './modules/tickets/TicketScreen.js';
import { ReportScreen } from './modules/tickets/ReportScreen.js';
import { TicketBoardScreen } from './modules/tickets/TicketBoardScreen.js';
import { TicketsScreen } from './modules/tickets/TicketsScreen.js';
import { ticketFilterSearch } from './modules/tickets/filters.js';
import { StatusClassificationScreen } from './modules/erp/StatusClassificationScreen.js';
import { ErpHealthScreen } from './modules/erp/ErpHealthScreen.js';
import { VehiclesScreen } from './modules/erp/VehiclesScreen.js';
import { PlansScreen } from './modules/erp/PlansScreen.js';
import { VehicleScreen } from './modules/erp/VehicleScreen.js';
import { BoletosScreen } from './modules/erp/BoletosScreen.js';
import { BoletoScreen } from './modules/erp/BoletoScreen.js';
import { ProductsScreen } from './modules/erp/ProductsScreen.js';
import { ProductVehiclesScreen } from './modules/erp/ProductVehiclesScreen.js';
import { MemberScreen as ErpMemberScreen } from './modules/erp/MemberScreen.js';
import { MembersScreen as ErpMembersScreen } from './modules/erp/MembersScreen.js';
import { VolunteersScreen as ErpVolunteersScreen } from './modules/erp/VolunteersScreen.js';
import { ServiceRecordsScreen } from './modules/erp/ServiceRecordsScreen.js';
import { ServiceRecordScreen } from './modules/erp/ServiceRecordScreen.js';
import { ChangesScreen } from './modules/erp/ChangesScreen.js';
import { ChangeScreen } from './modules/erp/ChangeScreen.js';
import { BoletoTypesScreen } from './modules/erp/BoletoTypesScreen.js';
import { SignInScreen } from './modules/platform/SignInScreen.js';
import { FirstAccessScreen } from './modules/platform/FirstAccessScreen.js';
import {
  AnalysisBoardScreen,
  CompensationBoardScreen,
  RepairBoardScreen,
} from './modules/claims/DepartmentBoardScreen.js';
import { RepairSupervisionScreen } from './modules/claims/supervision/RepairSupervisionScreen.js';
import { ClaimScreen } from './modules/claims/claim/ClaimScreen.js';
import { ClaimsScreen } from './modules/claims/claim/ClaimsScreen.js';
import { ThirdPartiesScreen } from './modules/claims/third-parties/ThirdPartiesScreen.js';
import { ThirdPartyScreen } from './modules/claims/third-parties/ThirdPartyScreen.js';
import { CompaniesScreen } from './modules/claims/companies/CompaniesScreen.js';
import { PhaseDeadlinesScreen } from './modules/claims/deadlines/PhaseDeadlinesScreen.js';
import { RotationsScreen } from './modules/claims/rotations/RotationsScreen.js';
import { PeripheralsScreen } from './modules/claims/peripherals/PeripheralsScreen.js';
import { ClosuresScreen } from './modules/claims/closures/ClosuresScreen.js';
import {
  SupervisorBoardScreen as ClaimExpensesBoardScreen,
  supervisedStages,
} from './modules/claims/expenses/SupervisorBoardScreen.js';
import { ManagementScreen } from './modules/claims/management/ManagementScreen.js';
import { PeripheralManagementScreen } from './modules/claims/management/PeripheralManagementScreen.js';
import { BeneficiariesScreen } from './modules/platform/BeneficiariesScreen.js';
import {
  PaymentsScreen,
  paymentCardSearch,
} from './modules/financeiro/PaymentsScreen.js';
import { RevenuesScreen } from './modules/financeiro/revenues/RevenuesScreen.js';
import { PaymentAccountsScreen } from './modules/financeiro/revenues/PaymentAccountsScreen.js';
import { ExpensesBoardScreen as FinanceExpensesScreen } from './modules/financeiro/ExpensesBoardScreen.js';
import { FinanceDashboardScreen } from './modules/financeiro/dashboard/FinanceDashboardScreen.js';
import { FinanceTeamMapScreen } from './modules/financeiro/team-map/FinanceTeamMapScreen.js';
import { ClaimsTeamMapScreen } from './modules/claims/team-map/ClaimsTeamMapScreen.js';
import {
  SaleExpensesScreen,
  saleExpenseCardSearch,
} from './modules/sales/SaleExpensesScreen.js';
import { LabelsScreen as RetentionLabelsScreen } from './modules/retention/LabelsScreen.js';
import { OperatorsScreen as RetentionOperatorsScreen } from './modules/retention/OperatorsScreen.js';
import { ReportScreen as RetentionReportScreen } from './modules/retention/ReportScreen.js';
import { BoardScreen } from './modules/retention/BoardScreen.js';
import { RequestScreen } from './modules/retention/RequestScreen.js';
import { MemberScreen } from './modules/portal/MemberScreen.js';
import { MembersScreen } from './modules/portal/MembersScreen.js';
import { DepartmentsScreen } from './modules/platform/DepartmentsScreen.js';
import { GroupsScreen } from './modules/platform/GroupsScreen.js';
import { HomeScreen } from './modules/platform/HomeScreen.js';
import { HOME_TAB_KEYS } from './panels.js';
import { AuditScreen } from './modules/platform/AuditScreen.js';
import { ContactBlockScreen } from './modules/platform/ContactBlockScreen.js';
import { TenantSettingsScreen } from './modules/platform/TenantSettingsScreen.js';
import { UsefulLinksScreen } from './modules/platform/UsefulLinksScreen.js';
import { DispatchesScreen as AssistanceDispatchesScreen } from './modules/assistance/DispatchesScreen.js';
import { AgendaScreen as AssistanceAgendaScreen } from './modules/assistance/AgendaScreen.js';
import {
  SETTLEMENT_TABS,
  SettlementsScreen as AssistanceSettlementsScreen,
} from './modules/assistance/SettlementsScreen.js';
import { ManagementScreen as AssistanceManagementScreen } from './modules/assistance/ManagementScreen.js';
import { ProgressSettingsScreen } from './modules/assistance/ProgressPanel.js';
import { UsageRulesScreen } from './modules/assistance/UsagePanel.js';
import { ServicesScreen } from './modules/assistance/ServicesScreen.js';
import { RecordsScreen as BreakRecordsScreen } from './modules/breaks/RecordsScreen.js';
import { OverrunsScreen as BreakOverrunsScreen } from './modules/breaks/OverrunsScreen.js';
import { SettingsScreen as BreakSettingsScreen } from './modules/breaks/SettingsScreen.js';
import { IntegrationsScreen } from './modules/platform/IntegrationsScreen.js';
import { PreferencesScreen } from './modules/platform/PreferencesScreen.js';
import { SessionsScreen } from './modules/platform/SessionsScreen.js';
import { PasswordScreen } from './modules/platform/PasswordScreen.js';
import {
  ForgotPasswordScreen,
  ResetPasswordScreen,
} from './modules/platform/PasswordRecoveryScreens.js';
import { UsersScreen } from './modules/platform/UsersScreen.js';
import { NotFoundScreen } from './modules/platform/NotFoundScreen.js';

export interface RouterContext {
  client: QueryClient;
}

/**
 * As rotas, escritas à mão — pelo mesmo motivo do menu.
 *
 * Módulo novo entra como um bloco aqui, apontando para as telas dele em
 * `modules/<modulo>/`, espelhando os módulos da API.
 */
const root = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
  // Sem estes dois, uma API fora do ar vira página em branco e um endereço
  // errado vira o erro cru do roteador.
  errorComponent: Failure,
  notFoundComponent: NotFound,
});

const signIn = createRoute({
  getParentRoute: () => root,
  path: '/sign-in',
  component: SignInScreen,
  beforeLoad: async ({ context }) => {
    // quem já tem sessão não vê a tela de login
    const session = await context.client.ensureQueryData(contextOptions);
    if (session) throw redirect({ to: '/' });
  },
});

/**
 * O token do convite chega na URL **uma vez**, e só aqui.
 *
 * É a única forma que um link de e-mail tem de carregar corpo num clique. A
 * partir daí ele nunca mais viaja assim: as rotas que o consomem são `POST`,
 * para o token não acabar em log de servidor nem no cabeçalho `Referer` da
 * próxima navegação. Mesmo desenho do portal.
 */
const withToken = z.object({ t: z.string().optional() });

/**
 * O primeiro acesso é PÚBLICO, e fica fora de `authenticated`.
 *
 * Pendurá-la sob a rota autenticada mandaria para o login exatamente quem não
 * consegue entrar — que é a definição de quem abre este link. E quem já tem
 * sessão e clica num convite antigo do e-mail vai para o início: era o que ele
 * queria.
 */
const firstAccess = createRoute({
  getParentRoute: () => root,
  path: '/first-access',
  component: FirstAccessScreen,
  validateSearch: withToken,
  beforeLoad: async ({ context }) => {
    const session = await context.client.ensureQueryData(contextOptions);
    if (session) throw redirect({ to: '/' });
  },
});

/**
 * A senha esquecida também é PÚBLICA (30/10/2026), pelo mesmo motivo do
 * primeiro acesso: quem precisa dela é quem não consegue entrar. Quem já tem
 * sessão vai para o início — para trocar a senha logado, há `/password`.
 */
const forgotPassword = createRoute({
  getParentRoute: () => root,
  path: '/forgot-password',
  component: ForgotPasswordScreen,
  beforeLoad: async ({ context }) => {
    const session = await context.client.ensureQueryData(contextOptions);
    if (session) throw redirect({ to: '/' });
  },
});

const resetPassword = createRoute({
  getParentRoute: () => root,
  path: '/reset-password',
  component: ResetPasswordScreen,
  validateSearch: withToken,
  beforeLoad: async ({ context }) => {
    const session = await context.client.ensureQueryData(contextOptions);
    if (session) throw redirect({ to: '/' });
  },
});

/**
 * Rota de layout sem caminho: tudo abaixo dela exige sessão.
 *
 * A verificação acontece ANTES de renderizar. É o mesmo motivo de o backend
 * negar por omissão: o caminho seguro precisa ser o caminho padrão.
 */
const authenticated = createRoute({
  getParentRoute: () => root,
  id: 'authenticated',
  component: Layout,
  beforeLoad: async ({ context }) => {
    const session = await context.client.ensureQueryData(contextOptions);
    if (!session) throw redirect({ to: '/sign-in' });
    return { session };
  },
});

/**
 * Guarda de rota para módulo que ainda não tem backend.
 *
 * Nas telas com API quem protege é o `PermissionGuard`: a tela abre e a
 * consulta devolve 403. Sinistros e Financeiro rodam sobre mock — não há
 * consulta para negar, e sem isto quem digitasse o endereço veria o módulo que
 * o menu escondeu. Quem não tem a permissão volta ao início. Esconder não é
 * proteger, e aqui não há dado no servidor a proteger; quando o backend nascer,
 * as rotas dele passam a exigir o mesmo recurso.
 */
function requires(resource: string, action: string) {
  return ({ context }: { context: { session: Parameters<typeof can>[0] } }) => {
    if (!can(context.session, resource, action)) throw redirect({ to: '/' });
  };
}

/**
 * Endereço errado de quem tem sessão: abre DENTRO do layout, com o menu — ver
 * `NotFoundScreen`.
 *
 * É uma rota curinga, e não `notFoundComponent` em `authenticated`: o roteador
 * só sobe até o `notFoundComponent` de uma rota que casou parte do endereço, e
 * uma rota de layout sem caminho nunca casa. Com ela lá, todo endereço errado
 * caía no da raiz, sem menu. A rota específica sempre vence a curinga.
 */
const notFound = createRoute({
  getParentRoute: () => authenticated,
  path: '$',
  component: NotFoundScreen,
});

const start = createRoute({
  getParentRoute: () => authenticated,
  path: '/',
  component: HomeScreen,
  /* A aba aberta do início; ausente ou desconhecida, a primeira que a pessoa vê. */
  validateSearch: z.object({
    panel: z.enum(HOME_TAB_KEYS).optional().catch(undefined),
  }),
});

const users = createRoute({
  getParentRoute: () => authenticated,
  path: '/users',
  component: UsersScreen,
});

const groups = createRoute({
  getParentRoute: () => authenticated,
  path: '/groups',
  component: GroupsScreen,
});

const departments = createRoute({
  getParentRoute: () => authenticated,
  path: '/departments',
  component: DepartmentsScreen,
});

const sessions = createRoute({
  getParentRoute: () => authenticated,
  path: '/sessions',
  component: SessionsScreen,
});

const password = createRoute({
  getParentRoute: () => authenticated,
  path: '/password',
  component: PasswordScreen,
});

const audit = createRoute({
  getParentRoute: () => authenticated,
  path: '/audit',
  component: AuditScreen,
});

/*
 * A única lista da interna que NÃO é escopada por base: a regra 15 decidiu que
 * o opt-out vale para todas. A tela diz isso em cima, num aviso fixo.
 */
/*
 * O cadastro de beneficiário — da plataforma, porque todo departamento
 * cadastra quem vai pagar. Sem `requires`: esta tela TEM API, e quem protege é
 * o `PermissionGuard` (`platform.beneficiaries:ver`); o menu esconde o item.
 */
const beneficiaries = createRoute({
  getParentRoute: () => authenticated,
  path: '/beneficiaries',
  component: BeneficiariesScreen,
});

const contactBlock = createRoute({
  getParentRoute: () => authenticated,
  path: '/contact-blocks',
  component: ContactBlockScreen,
});

/* A configuração da Assistência 24h, no menu da Plataforma (01/11/2026). */
const assistanceQuotas = createRoute({
  getParentRoute: () => authenticated,
  path: '/assistance-settings/quotas',
  beforeLoad: requires('assistance.dispatches', 'configurar'),
  component: UsageRulesScreen,
});
const assistanceDeadlines = createRoute({
  getParentRoute: () => authenticated,
  path: '/assistance-settings/deadlines',
  beforeLoad: requires('assistance.dispatches', 'configurar'),
  component: ProgressSettingsScreen,
});
/* Os serviços de cada tipo (02/11/2026). */
const assistanceServicesRoute = createRoute({
  getParentRoute: () => authenticated,
  path: '/assistance-settings/services',
  beforeLoad: requires('assistance.dispatches', 'configurar'),
  component: ServicesScreen,
});

/* Configuração é da BASE — o guard cobra `plataforma.tenants`. */
const tenantSetting = createRoute({
  getParentRoute: () => authenticated,
  path: '/tenant-settings',
  component: TenantSettingsScreen,
});

/*
 * Os links úteis da base: todo mundo logado vê, como as sessões e as
 * preferências — o cadastro mora em `/tenant-settings`.
 */
const usefulLinks = createRoute({
  getParentRoute: () => authenticated,
  path: '/useful-links',
  component: UsefulLinksScreen,
});

/*
 * O intervalo (09/10/2026). A relação é de todo mundo logado — quem não tem
 * `breaks.records:ver` vê só os próprios, e quem decide é o servidor.
 */
const breakRecords = createRoute({
  getParentRoute: () => authenticated,
  path: '/breaks/records',
  component: BreakRecordsScreen,
});
const breakOverruns = createRoute({
  getParentRoute: () => authenticated,
  path: '/breaks/overruns',
  component: BreakOverrunsScreen,
});
/*
 * Endereço antigo dos prestadores da Assistência 24h (até 30/10/2026): o
 * prestador é a própria empresa, e a forma de pagamento, o raio e a tabela de
 * preço são editados na ação "Assistência 24h" da lista de Empresas.
 */
const assistanceProviders = createRoute({
  getParentRoute: () => authenticated,
  path: '/assistance/providers',
  beforeLoad: () => {
    throw redirect({ to: '/companies' });
  },
});
/*
 * A tabela única da central (29/10/2026): `?status=` é o filtro de status,
 * `?open=` abre o acionamento por cima da lista — o link da origem
 * da despesa no Financeiro —, `?novo=` abre o modal do novo acionamento e
 * `?draft=` continua um rascunho nele (`assistencia-22`, P15).
 */
const assistanceDispatches = createRoute({
  getParentRoute: () => authenticated,
  path: '/assistance/dispatches',
  validateSearch: z.object({
    status: z.enum(DISPATCH_LIST_FILTERS).optional().catch(undefined),
    open: z.coerce.number().int().positive().optional().catch(undefined),
    novo: z.enum(['agora', 'agendar']).optional().catch(undefined),
    draft: z.coerce.number().int().positive().optional().catch(undefined),
  }),
  component: function AssistanceDispatchesRoute() {
    return <AssistanceDispatchesScreen search={assistanceDispatches.useSearch()} />;
  },
});
/* As duas páginas de antes viraram o modal e o filtro de rascunhos; o endereço salvo continua chegando. */
const legacyAssistanceNew = createRoute({
  getParentRoute: () => authenticated,
  path: '/assistance/new',
  validateSearch: z.object({
    draft: z.coerce.number().int().positive().optional().catch(undefined),
  }),
  beforeLoad: ({ search }) => {
    throw redirect({
      to: '/assistance/dispatches',
      search: search.draft
        ? { status: 'rascunho', draft: search.draft }
        : { novo: 'agora' },
    });
  },
});
const assistanceAgenda = createRoute({
  getParentRoute: () => authenticated,
  path: '/assistance/agenda',
  component: AssistanceAgendaScreen,
});
const legacyAssistanceDrafts = createRoute({
  getParentRoute: () => authenticated,
  path: '/assistance/drafts',
  beforeLoad: () => {
    throw redirect({ to: '/assistance/dispatches', search: { status: 'rascunho' } });
  },
});
/*
 * Pagamentos (02/11/2026): `?aba=aprovacao` é a aprovação do supervisor, que
 * até então era a tela Despesas (`/assistance/expenses`, que só redireciona).
 */
const assistanceSettlements = createRoute({
  getParentRoute: () => authenticated,
  path: '/assistance/settlements',
  validateSearch: z.object({
    aba: z.enum(SETTLEMENT_TABS).optional().catch(undefined),
  }),
  component: function AssistanceSettlementsRoute() {
    const navigate = useNavigate();
    const { aba } = assistanceSettlements.useSearch();
    return (
      <AssistanceSettlementsScreen
        tab={aba}
        onTab={(tab) =>
          void navigate({
            to: '/assistance/settlements',
            search: tab === 'a_fechar' ? {} : { aba: tab },
            replace: true,
          })
        }
      />
    );
  },
});
/*
 * Endereço antigo do quadro do supervisor (até 02/11/2026): virou a aba
 * "Aprovação do supervisor" de Pagamentos, e só redireciona.
 */
const assistanceExpenses = createRoute({
  getParentRoute: () => authenticated,
  path: '/assistance/expenses',
  beforeLoad: () => {
    throw redirect({ to: '/assistance/settlements', search: { aba: 'aprovacao' } });
  },
});
/* A Gestão da assistência (`assistencia-35`, 30/10/2026): o andamento, o mapa e os tipos. */
const assistanceManagement = createRoute({
  getParentRoute: () => authenticated,
  path: '/assistance/management',
  component: AssistanceManagementScreen,
  beforeLoad: requires('assistance.management', 'ver'),
});
const breakSettings = createRoute({
  getParentRoute: () => authenticated,
  path: '/breaks/settings',
  component: BreakSettingsScreen,
});

/*
 * Credencial de integração — recurso próprio, e não `plataforma.tenants`.
 *
 * Quem ajusta a sigla da base no menu não é quem aplica a credencial que dá
 * escrita no ERP de produção.
 */
const integrations = createRoute({
  getParentRoute: () => authenticated,
  path: '/integrations',
  component: IntegrationsScreen,
  /* A integração aberta; ausente ou desconhecida, a tela abre a primeira da lista. */
  validateSearch: z.object({
    integration: z.string().max(40).optional().catch(undefined),
  }),
});

/* Preferência é da própria pessoa: sem `recurso`, como as sessões dela. */
const preferences = createRoute({
  getParentRoute: () => authenticated,
  path: '/preferences',
  component: PreferencesScreen,
});

/*
 * O bloco de demandas. `/tickets/categories` vem ANTES de `/tickets/$id`
 * porque o TanStack Router resolve segmento fixo antes de parâmetro — e a ordem
 * aqui é a que a leitura humana confere. Invertido, `/tickets/categories`
 * abriria a tela de detalhe com `id = "categories"`.
 */
const tickets = createRoute({
  getParentRoute: () => authenticated,
  path: '/tickets',
  component: TicketsScreen,
  /*
   * O recorte da mesa mora no ENDEREÇO desde 17/09 — ver `useTicketFilters`.
   * Sem isto o `useSearch` da mesa devolveria sempre vazio e o filtro não
   * sobreviveria a um reload, que é metade do motivo de ele ter saído do
   * `useState`.
   */
  validateSearch: ticketFilterSearch,
});

const ticketCategories = createRoute({
  getParentRoute: () => authenticated,
  path: '/tickets/categories',
  component: CategoriesScreen,
});

const ticketReport = createRoute({
  getParentRoute: () => authenticated,
  path: '/tickets/report',
  component: ReportScreen,
  /* O relatório filtra pelo MESMO gancho que a mesa — ver `filters.tsx`. */
  validateSearch: ticketFilterSearch,
});

/* o quadro: a mesma mesa, com as situações como colunas — o MESMO recorte no endereço */
const ticketBoard = createRoute({
  getParentRoute: () => authenticated,
  path: '/tickets/board',
  component: TicketBoardScreen,
  validateSearch: ticketFilterSearch,
});

const ticket = createRoute({
  getParentRoute: () => authenticated,
  path: '/tickets/$id',
  component: TicketScreen,
});

/*
 * O bloco do erp: a saúde da integração e a classificação de situações.
 *
 * As duas ficam sob `/erp/*` e nenhuma aceita a base pelo endereço — a praça é
 * a da sessão, resolvida no servidor. Uma rota que aceitasse `?tenant=` seria
 * uma praça lendo a outra por uma troca de número, e a permissão da casa é por
 * grupo, que pertence a um tenant.
 */
const erpHealth = createRoute({
  getParentRoute: () => authenticated,
  path: '/erp/saude',
  component: ErpHealthScreen,
});

const statusesClassification = createRoute({
  getParentRoute: () => authenticated,
  path: '/erp/classificacoes',
  component: StatusClassificationScreen,
  validateSearch: tablePages(['pendentes', 'classificadas']),
});

/*
 * O cadastro. `/erp/veiculos` vem ANTES de `/erp/veiculos/$code` pelo mesmo
 * motivo de `/tickets/categories`: o casamento é por ordem de registro, e
 * invertido a lista cairia no detalhe com `code = undefined` — sem nada no
 * código parecendo errado.
 *
 * Nenhuma delas recebe placa, chassi ou documento no endereço: a busca é `POST`
 * com o valor no corpo, e o `$code` é o código da CASA (`VE-000123`). Era o
 * `id` do espelho até 05/10/2026, e trocá-lo conserta um defeito medido:
 * `external.sga_*` é refeito por `replaceSlice`, que apaga a fatia da praça e
 * reinsere, então todo `id` de lá muda a cada varredura completa — um link
 * salvo abria outro veículo no dia seguinte.
 */
const erpVehicles = createRoute({
  getParentRoute: () => authenticated,
  path: '/erp/veiculos',
  component: VehiclesScreen,
  /* o plano que `/erp/planos` abre — o código do grupo de produto do ERP, nunca a placa */
  validateSearch: listSearch({ situacao: textParam(20), plano: textParam(20) }),
});

const erpVehicle = createRoute({
  getParentRoute: () => authenticated,
  path: '/erp/veiculos/$code',
  component: VehicleScreen,
  validateSearch: tablePages(['produtos', 'boletos', 'alteracoes']),
});

const erpPlans = createRoute({
  getParentRoute: () => authenticated,
  path: '/erp/planos',
  component: PlansScreen,
  validateSearch: listSearch({}),
});

const erpBoletos = createRoute({
  getParentRoute: () => authenticated,
  path: '/erp/boletos',
  component: BoletosScreen,
  validateSearch: listSearch({
    situacao: textParam(20),
    pago: textParam(10),
    dueFrom: textParam(10),
    dueUntil: textParam(10),
  }),
});

/* depois da lista, pelo mesmo motivo de `/erp/veiculos/$code` */
const erpBoleto = createRoute({
  getParentRoute: () => authenticated,
  path: '/erp/boletos/$code',
  component: BoletoScreen,
  validateSearch: tablePages(['veiculos', 'alteracoes']),
});

const erpProducts = createRoute({
  getParentRoute: () => authenticated,
  path: '/erp/produtos',
  component: ProductsScreen,
  validateSearch: listSearch({}),
});

/* depois da lista, pelo mesmo motivo de `/erp/veiculos/$code` */
const erpProductVehicles = createRoute({
  getParentRoute: () => authenticated,
  path: '/erp/produtos/$code',
  component: ProductVehiclesScreen,
  validateSearch: listSearch({}),
});

/*
 * O histórico do SGA (14/10/2026): os atendimentos, as alterações e os tipos de
 * boleto, e a ficha do associado — `$code` é o `AS-` da casa ou o código do ERP.
 */
const erpServiceRecords = createRoute({
  getParentRoute: () => authenticated,
  path: '/erp/atendimentos',
  component: ServiceRecordsScreen,
  validateSearch: listSearch({
    from: textParam(10),
    until: textParam(10),
    typeCode: textParam(20),
    statusCode: textParam(20),
    departmentCode: textParam(20),
  }),
});

/* depois da lista, pelo mesmo motivo de `/erp/veiculos/$code`; `$code` é o `codigo_atendimento` do SGA */
const erpServiceRecord = createRoute({
  getParentRoute: () => authenticated,
  path: '/erp/atendimentos/$code',
  component: ServiceRecordScreen,
});

const erpChanges = createRoute({
  getParentRoute: () => authenticated,
  path: '/erp/alteracoes',
  component: ChangesScreen,
  validateSearch: listSearch({
    subject: textParam(20),
    field: textParam(60),
    from: textParam(10),
    until: textParam(10),
  }),
});

/* depois da lista; o código da alteração só é único dentro do assunto */
const erpChange = createRoute({
  getParentRoute: () => authenticated,
  path: '/erp/alteracoes/$subject/$code',
  component: ChangeScreen,
  validateSearch: tablePages(['historico']),
});

const erpBoletoTypes = createRoute({
  getParentRoute: () => authenticated,
  path: '/erp/tipos-boleto',
  component: BoletoTypesScreen,
  validateSearch: tablePages(['pagina']),
});

/* a lista ANTES da ficha, pelo mesmo motivo de `/erp/veiculos/$code` */
const erpMembers = createRoute({
  getParentRoute: () => authenticated,
  path: '/erp/associados',
  component: ErpMembersScreen,
  validateSearch: listSearch({ situacao: textParam(80), tipoPessoa: textParam(20) }),
});

const erpMember = createRoute({
  getParentRoute: () => authenticated,
  path: '/erp/associados/$code',
  component: ErpMemberScreen,
  validateSearch: tablePages([
    'beneficiarios',
    'atendimentos',
    'veiculos',
    'boletos',
    'alteracoes',
  ]),
});

/* os voluntários do cadastro (30/10/2026) — sem ficha: a linha não abre nada */
const erpVolunteers = createRoute({
  getParentRoute: () => authenticated,
  path: '/erp/voluntarios',
  component: ErpVolunteersScreen,
  validateSearch: listSearch({ link: textParam(20) }),
});

/*
 * O bloco do Kanban Cancelamento (30/10/2026): o quadro, o cartão aberto, a
 * escala, as etiquetas e o relatório. O quadro junta CE e GO, como a Mesa; o
 * cartão aberto leva a base na busca, e a API confere a base em cada rota
 * (`@TenantFromPath`) — a busca escolhe a base, não concede acesso a ela.
 */
/*
 * O sinistro — a lista e a ficha, sobre a API, e por isso cobram o recurso do
 * ato (`claims.notices:ver`). Substituíram o quadro do Aviso em 24/09.
 */
const claimsList = createRoute({
  getParentRoute: () => authenticated,
  path: '/claims',
  component: ClaimsScreen,
  beforeLoad: requires('claims.notices', 'ver'),
});

const claimsDetail = createRoute({
  getParentRoute: () => authenticated,
  path: '/claims/$id',
  component: ClaimScreen,
  beforeLoad: requires('claims.notices', 'ver'),
});

/*
 * Os três departamentos de Sinistro com mesa — a Análise, o Reparo e, desde
 * 09/10/2026, a Indenização —, sobre a API. A mesma tela
 * (`modules/claims/DepartmentBoardScreen.tsx`), em quadro ou tabela; abrir um
 * card é a mesa, por cima da lista, e não navega.
 */

const claimsAnalysisBoard = createRoute({
  getParentRoute: () => authenticated,
  path: '/claims/analysis/board',
  component: AnalysisBoardScreen,
  beforeLoad: requires('claims.module', 'ver'),
});

const claimsRepairBoard = createRoute({
  getParentRoute: () => authenticated,
  path: '/claims/repair/board',
  component: RepairBoardScreen,
  beforeLoad: requires('claims.module', 'ver'),
});

/*
 * Supervisão › Supervisão do Reparo (29/10/2026): as duas etapas do supervisor,
 * em duas filas — não um quadro.
 */
const claimsRepairSupervisionBoard = createRoute({
  getParentRoute: () => authenticated,
  path: '/claims/repair/supervision',
  component: RepairSupervisionScreen,
  beforeLoad: requires('claims.repairs', 'supervisionar'),
});

const claimsCompensationBoard = createRoute({
  getParentRoute: () => authenticated,
  path: '/claims/compensation/board',
  component: CompensationBoardScreen,
  beforeLoad: requires('claims.module', 'ver'),
});

/*
 * Os terceiros — a primeira tela de Sinistros sobre a API, e por isso cobra o
 * recurso do ato (`claims.third_parties:ver`), não só o do módulo.
 */
const claimsThirdParties = createRoute({
  getParentRoute: () => authenticated,
  path: '/claims/third-parties',
  component: ThirdPartiesScreen,
  beforeLoad: requires('claims.third_parties', 'ver'),
});

const claimsThirdParty = createRoute({
  getParentRoute: () => authenticated,
  path: '/claims/third-parties/$id',
  component: ThirdPartyScreen,
  beforeLoad: requires('claims.third_parties', 'ver'),
});

/*
 * Empresas e Instituições — a mesma tabela (`companies`), em duas
 * telas: o grupo é só do front (decidido em 25/09). As duas cobram o recurso
 * do ato, `claims.companies:ver`.
 *
 * Saíram de Sinistros para Plataforma → Cadastros em 28/10/2026 (pedido do
 * dono): a tabela é da casa, e a Assistência 24h já a lê. O recurso continua
 * `claims.companies` — renomeá-lo mexe nas concessões gravadas, e é outra
 * leva. Os caminhos antigos redirecionam, porque link salvo e aviso gravado
 * apontam para eles.
 */
const companiesRoute = createRoute({
  getParentRoute: () => authenticated,
  path: '/companies',
  component: () => <CompaniesScreen category="prestadores" />,
  beforeLoad: requires('claims.companies', 'ver'),
});

const institutions = createRoute({
  getParentRoute: () => authenticated,
  path: '/institutions',
  component: () => <CompaniesScreen category="instituicoes" />,
  beforeLoad: requires('claims.companies', 'ver'),
});

const legacyClaimsProviders = createRoute({
  getParentRoute: () => authenticated,
  path: '/claims/providers',
  beforeLoad: () => {
    throw redirect({ to: '/companies' });
  },
});

const legacyClaimsInstitutions = createRoute({
  getParentRoute: () => authenticated,
  path: '/claims/institutions',
  beforeLoad: () => {
    throw redirect({ to: '/institutions' });
  },
});

/*
 * Periféricos (08/10/2026) — a primeira mesa de Sinistros que não é de
 * sinistro: o acionamento tem rota e permissão próprias
 * (`claims.peripherals:ver`).
 */
const claimsPeripherals = createRoute({
  getParentRoute: () => authenticated,
  path: '/claims/peripherals',
  /* `?open=` abre o acionamento — o link da origem da despesa no Financeiro. */
  validateSearch: z.object({
    open: z.coerce.number().int().positive().optional().catch(undefined),
  }),
  component: function PeripheralsRoute() {
    const { open } = claimsPeripherals.useSearch();
    return <PeripheralsScreen initialOpen={open} />;
  },
  beforeLoad: requires('claims.peripherals', 'ver'),
});

/*
 * Encerrados (13/10/2026) — os concluídos, as desistências e as negativas de
 * todos os departamentos e de Periféricos, com o recurso do ATO
 * (`claims.closures:ver`).
 */
const claimsClosures = createRoute({
  getParentRoute: () => authenticated,
  path: '/claims/closed',
  component: ClosuresScreen,
  beforeLoad: requires('claims.closures', 'ver'),
});

/*
 * O quadro do supervisor da despesa (26/10/2026) — uma rota para os cinco
 * departamentos: entra quem supervisiona algum deles, e a tela mostra só os
 * dele. Cada departamento cobra o próprio recurso na API.
 */
const claimsExpenses = createRoute({
  getParentRoute: () => authenticated,
  path: '/claims/expenses',
  component: ClaimExpensesBoardScreen,
  beforeLoad: ({ context }) => {
    if (supervisedStages(context.session).length === 0) throw redirect({ to: '/' });
  },
});

/*
 * A Gestão do sinistro (23/10/2026) — o painel e o mapa da Análise, do Reparo
 * e da Indenização, com o recurso do ATO (`claims.management:ver`). Periféricos
 * saiu para a sua Gestão em 28/10/2026.
 */
const claimsManagement = createRoute({
  getParentRoute: () => authenticated,
  path: '/claims/management',
  component: ManagementScreen,
  beforeLoad: requires('claims.management', 'ver'),
  /* A sub-aba: ausente é o Painel, a principal; `linhas` é o fluxo em linhas (26/10/2026). */
  validateSearch: z.object({
    view: z.enum(['linhas']).optional().catch(undefined),
  }),
});

/*
 * A Gestão dos periféricos (28/10/2026, pedido do dono): o painel e o fluxo em
 * linhas de Periféricos, sob o mesmo recurso da Gestão do sinistro — quem
 * acompanha um acompanha o outro.
 */
const claimsPeripheralManagement = createRoute({
  getParentRoute: () => authenticated,
  path: '/claims/management/peripherals',
  component: PeripheralManagementScreen,
  beforeLoad: requires('claims.management', 'ver'),
  /* A sub-aba: ausente é o painel; `linhas` é o fluxo em linhas. */
  validateSearch: z.object({
    view: z.enum(['linhas']).optional().catch(undefined),
  }),
});

/* O SLA de cada fase (07/10/2026) — de quem configura os prazos. */
const claimsPhaseDeadlines = createRoute({
  getParentRoute: () => authenticated,
  path: '/claims/phase-deadlines',
  component: PhaseDeadlinesScreen,
  beforeLoad: requires('claims.deadlines', 'configurar'),
});

/* O rodízio de responsáveis (20/10/2026) — de quem o configura. */
const claimsRotations = createRoute({
  getParentRoute: () => authenticated,
  path: '/claims/rotations',
  component: RotationsScreen,
  beforeLoad: requires('claims.rotations', 'configurar'),
});

/*
 * A receita (29/10/2026), que tomou o lugar das Cobranças: a cobrança no Asaas
 * virou um ato sobre a receita. Sem `requires`: a tela TEM API, e quem
 * protege é o `PermissionGuard` — sem `finance.revenues:ver` a consulta
 * devolve 403, e o menu esconde o item.
 */
const financeRevenues = createRoute({
  getParentRoute: () => authenticated,
  path: '/finance/revenues',
  component: RevenuesScreen,
});

/* As contas de recebimento — por onde o dinheiro da receita entra. */
const financePaymentAccounts = createRoute({
  getParentRoute: () => authenticated,
  path: '/finance/payment-accounts',
  component: PaymentAccountsScreen,
});

/* O endereço antigo das Cobranças, para link salvo e aviso gravado. */
const financeChargesRedirect = createRoute({
  getParentRoute: () => authenticated,
  path: '/finance/charges',
  beforeLoad: () => {
    throw redirect({ to: '/finance/revenues' });
  },
});

/*
 * O painel do Financeiro (29/10/2026): a receita e a despesa por cidade,
 * consultor e veículo. Sem `requires`: a tela TEM API, e quem protege é o
 * `PermissionGuard` — sem `finance.dashboard:ver` a consulta devolve 403, e o
 * menu esconde o item.
 */
const financeDashboard = createRoute({
  getParentRoute: () => authenticated,
  path: '/finance/dashboard',
  component: FinanceDashboardScreen,
});

/*
 * O mapa do time (29/10/2026), nos dois módulos que o leem: a receita e a
 * despesa no Financeiro, as ocorrências e a sinistralidade no Sinistro. Sem
 * `requires`, como o painel: a tela TEM API, e quem protege é o
 * `PermissionGuard` (`finance.team_map:ver`, `claims.team_map:ver`).
 */
const financeTeamMap = createRoute({
  getParentRoute: () => authenticated,
  path: '/finance/team-map',
  component: FinanceTeamMapScreen,
});
const claimsTeamMap = createRoute({
  getParentRoute: () => authenticated,
  path: '/claims/management/team-map',
  component: ClaimsTeamMapScreen,
});

/*
 * O quadro de despesas do Financeiro (`financeiro-03`, 26/10/2026): o
 * sinistro e a assistência que o supervisor aprovou.
 */
const financeExpenses = createRoute({
  getParentRoute: () => authenticated,
  path: '/finance/expenses',
  component: FinanceExpensesScreen,
  beforeLoad: requires('finance.expenses', 'ver'),
});

/*
 * O pagamento das premiações. Sem `requires`: esta tela TEM API, e quem
 * protege é o `PermissionGuard` — a consulta de quem não tem
 * `management.payments:ver` devolve 403, e o menu esconde o item.
 */
const recurrencePayments = createRoute({
  getParentRoute: () => authenticated,
  path: '/sales/recurrence-payments',
  component: () => <PaymentsScreen scope="praca" />,
  validateSearch: paymentCardSearch,
});

/**
 * O MESMO quadro, com os PRÓPRIOS cartões — `/sales/recurrence-payments/mine`, sob
 * `management.payments:ver_meus`. Endereço próprio, e não um filtro na busca
 * do outro: as rotas da API são outras (`/payments/mine/board`), e quem tem
 * só `ver_meus` não pode cair na leitura da praça por um parâmetro esquecido.
 *
 * `?card=` abre um cartão: é para onde o sino leva o aviso do pagamento.
 */
const myRecurrencePayments = createRoute({
  getParentRoute: () => authenticated,
  path: '/sales/recurrence-payments/mine',
  component: () => <PaymentsScreen scope="meus" />,
  validateSearch: paymentCardSearch,
});

/**
 * As despesas lançadas a partir das vendas (02/10/2026) — o quadro da praça,
 * sob `sales.expenses:ver`, e o do consultor, só com as dele, sob
 * `:ver_minhas`. Endereços próprios pelo motivo de `myRecurrencePayments`: as
 * rotas da API são outras. `?card=` abre um cartão — é para onde o sino leva.
 */
const saleExpenses = createRoute({
  getParentRoute: () => authenticated,
  path: '/sales/expenses',
  component: () => <SaleExpensesScreen scope="praca" />,
  validateSearch: saleExpenseCardSearch,
});

const mySaleExpenses = createRoute({
  getParentRoute: () => authenticated,
  path: '/sales/expenses/mine',
  component: () => <SaleExpensesScreen scope="minhas" />,
  validateSearch: saleExpenseCardSearch,
});

const retentionBoard = createRoute({
  getParentRoute: () => authenticated,
  path: '/retention/board',
  component: BoardScreen,
});

/* A configuração e o relatório do Kanban Cancelamento (30/10/2026). */
const retentionOperators = createRoute({
  getParentRoute: () => authenticated,
  path: '/retention/operators',
  component: RetentionOperatorsScreen,
});

const retentionLabels = createRoute({
  getParentRoute: () => authenticated,
  path: '/retention/labels',
  component: RetentionLabelsScreen,
});

const retentionReport = createRoute({
  getParentRoute: () => authenticated,
  path: '/retention/report',
  component: RetentionReportScreen,
});

/*
 * O cartão aberto. A base vai na busca (`?tenant=ce`): o quadro junta CE e GO,
 * e o mesmo número de cartão pode existir nas duas. Sem ela, vale a base da
 * sessão — o caminho da ficha do veículo, que é de uma base só.
 */
const retentionRequest = createRoute({
  getParentRoute: () => authenticated,
  path: '/retention/requests/$id',
  component: RequestScreen,
  validateSearch: z.object({ tenant: z.string().min(1).max(40).optional() }),
});

/*
 * O bloco da cobrança: a mesa, a esteira e a régua da praça.
 *
 * Os três caminhos são fixos e não colidem entre si — não há rota de detalhe
 * aqui, e a ausência é da própria mesa: o cartão abre num painel ao lado da
 * lista, sem endereço próprio. Um `/billing/desk/$id` prometeria uma
 * página que ninguém escreveu, e o painel perderia o contexto da coluna, que é
 * o que faz a mesa ser mesa.
 *
 * Nenhuma delas aceita a base pelo endereço: a praça é a da sessão, resolvida no
 * servidor. Uma rota que aceitasse `?tenant=` seria uma praça pausando a
 * cobrança da outra por uma troca de número — e pausar é o ato deste módulo cuja
 * consequência não cabe num atendimento.
 */
const billingDesk = createRoute({
  getParentRoute: () => authenticated,
  path: '/billing/desk',
  component: DeskBoardScreen,
});

/*
 * A Mesa que substitui o `KanbanCobranca` (19/10/2026) — o quadro em
 * `/billing/desk`, a importação de inadimplentes e a configuração. A Mesa
 * antiga (`DeskScreen.tsx`) saiu da rota e é apagada quando a esteira mesclar.
 *
 * Estas telas não aceitam a base pelo endereço: o quadro mostra todas as bases
 * da pessoa juntas, e a importação e a configuração escolhem a base num campo.
 */
const billingDeskImport = createRoute({
  getParentRoute: () => authenticated,
  path: '/billing/desk/import',
  component: DeskImportScreen,
});

const billingDeskSettings = createRoute({
  getParentRoute: () => authenticated,
  path: '/billing/desk/settings',
  component: DeskSettingsScreen,
});

/* O aproveitamento da equipe — o "Dashboard de Aproveitamento" do Kanban (20/10/2026). */
const billingDeskDashboard = createRoute({
  getParentRoute: () => authenticated,
  path: '/billing/desk/dashboard',
  component: DeskDashboardScreen,
});

/* As movimentações dos cards, com as do Kanban (20/10/2026). */
const billingDeskMoves = createRoute({
  getParentRoute: () => authenticated,
  path: '/billing/desk/moves',
  component: DeskMovesScreen,
});

/*
 * A premiação da cobrança (21/10/2026), que substitui a `PremiacaoCobrancaRetencao`:
 * o painel com o relatório de pagamento, o monitoramento, os dados, o log e a
 * configuração. Como a Mesa, a base não vem no endereço: o painel soma as
 * bases da pessoa, e as outras telas escolhem a base num campo.
 */
const billingAwards = createRoute({
  getParentRoute: () => authenticated,
  path: '/billing/awards',
  component: AwardsScreen,
  validateSearch: z.object({
    period: z
      .string()
      .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
      .optional()
      .catch(undefined),
  }),
});

/*
 * Uma linha por competência registrada — os meses trazidos do sistema antigo,
 * com o valor congelado, e os daqui (pedido do dono, 23/10/2026).
 */
const billingAwardsHistory = createRoute({
  getParentRoute: () => authenticated,
  path: '/billing/awards/history',
  component: AwardsHistoryScreen,
});

const billingAwardsLinks = createRoute({
  getParentRoute: () => authenticated,
  path: '/billing/awards/links',
  component: AwardsLinksScreen,
});

const billingAwardsData = createRoute({
  getParentRoute: () => authenticated,
  path: '/billing/awards/data',
  component: AwardsDataScreen,
});

const billingAwardsLog = createRoute({
  getParentRoute: () => authenticated,
  path: '/billing/awards/log',
  component: AwardsLogScreen,
});

const billingAwardsSettings = createRoute({
  getParentRoute: () => authenticated,
  path: '/billing/awards/settings',
  component: AwardsSettingsScreen,
});

/*
 * Um operador na competência — a tela do operador da `PremiacaoCobrancaRetencao`
 * (23/10/2026). Chega-se a ela pelo nome no painel; a competência vem na busca.
 */
const billingAwardsOperator = createRoute({
  getParentRoute: () => authenticated,
  path: '/billing/awards/operator/$operatorId',
  component: AwardsOperatorScreen,
  validateSearch: z.object({
    period: z
      .string()
      .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
      .optional()
      .catch(undefined),
  }),
});

const billingPipeline = createRoute({
  getParentRoute: () => authenticated,
  path: '/billing/pipeline',
  component: PipelineScreen,
});

/*
 * A régua fica sob `billing.tiers`, que é recurso separado de propósito: quem
 * opera a mesa não muda o que a praça inteira dispara sozinha. Por isso ela é
 * item de "Configuração" no menu, e não item de primeira linha ao lado da mesa.
 */
const billingLadder = createRoute({
  getParentRoute: () => authenticated,
  path: '/billing/tiers',
  component: TiersScreen,
});

/*
 * O bloco do comercial: sete telas, sete recursos, nenhum endereço de detalhe.
 *
 * Os sete caminhos são fixos e não colidem entre si. A ausência de
 * `/sales/sales/$id` é da própria lista: a correção de uma venda acontece
 * no formulário ao lado dela, e um endereço de detalhe prometeria uma página
 * que ninguém escreveu.
 *
 * **Um recurso por tela, e é por isso que são sete rotas e não uma com abas.**
 * Ver vendas, ver metas, ver o ranking, ver a comissão apurada de todo mundo,
 * ver o livro-caixa, manter o cadastro que decide de quem é a comissão e mexer
 * na régua que decide quanto cada placa paga são concessões de tamanhos
 * diferentes — o catálogo do módulo as separou pelo tamanho da consequência.
 * Uma tela só com abas daria a alguém a primeira aba funcionando e as outras
 * seis devolvendo 403.
 *
 * A exceção é o cadastro: consultores, times e hierarquia ficam em UMA tela,
 * sob `sales.teams`, porque o contrato os pôs sob um recurso só. A regra é a
 * mesma nos dois sentidos — a fronteira da tela acompanha a fronteira da
 * permissão, nunca a do assunto.
 *
 * Nenhuma delas aceita a base pelo endereço: a praça é a da sessão, resolvida no
 * servidor. Uma rota que aceitasse `?tenant=` seria uma praça lendo a comissão
 * apurada da outra por uma troca de número — e aqui o que vaza é quanto cada
 * pessoa nomeada recebeu.
 */
/*
 * O Início do Comercial (28/10/2026) — as premiações do consultor no mês, com a
 * nota fiscal de cada lote. A competência viaja na busca desde a faixa de meses,
 * para que o relatório da premiação abra no mês escolhido e a volta caia nele.
 *
 * `consultant` abre o Início de OUTRA pessoa, para quem tem a ação larga — o
 * papel que o painel de outro consultor tinha. O código fica na busca, e não no
 * caminho, pelo motivo de `contract.test.ts`: fora do caminho, ele não entra
 * em log de servidor nem em cabeçalho `Referer`. O número também vale: o
 * roteador lê a busca como JSON, e `consultant=394` chega como o NÚMERO 394.
 */
const optionalCode = z
  .union([z.string(), z.number().int().nonnegative()])
  .transform(String)
  .pipe(z.string().min(1).max(40))
  .optional()
  .catch(undefined);
const optionalMonth = z
  .string()
  .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
  .optional()
  .catch(undefined);

const salesHome = createRoute({
  getParentRoute: () => authenticated,
  path: '/sales/home',
  component: SalesHomeScreen,
  validateSearch: z.object({ month: optionalMonth, consultant: optionalCode }),
});

/*
 * O PAINEL DE GESTÃO saiu em 28/10/2026 — o Início assumiu o papel dele. Os três
 * endereços continuam existindo só para redirecionar: avisos antigos, links
 * salvos e o histórico do navegador apontam para eles, e um 404 ali seria a
 * tela falhando sem dizer por quê.
 */
const salesManagementPanel = createRoute({
  getParentRoute: () => authenticated,
  path: '/sales/management',
  beforeLoad: () => {
    throw redirect({ to: '/sales/home', replace: true });
  },
});

const salesConsultantPanel = createRoute({
  getParentRoute: () => authenticated,
  path: '/sales/management/me',
  validateSearch: z.object({ month: optionalMonth }),
  beforeLoad: ({ search }) => {
    throw redirect({ to: '/sales/home', search: { month: search.month }, replace: true });
  },
});

const salesOtherConsultantPanel = createRoute({
  getParentRoute: () => authenticated,
  path: '/sales/management/consultant',
  validateSearch: z.object({ consultant: optionalCode, month: optionalMonth }),
  beforeLoad: ({ search }) => {
    throw redirect({
      to: '/sales/home',
      search: { consultant: search.consultant, month: search.month },
      replace: true,
    });
  },
});

/**
 * A CARTEIRA — `/sales/metrics?state=garantido|perdendo`.
 *
 * ## O lado viaja na BUSCA, e a competência não
 *
 * O lado é o assunto do link que se manda para alguém ("olha o que estamos
 * perdendo"), então ele precisa estar no endereço. A competência fica no
 * componente porque a conferência não acontece aqui — ela acontece no relatório
 * de cada premiação, e é para lá que o mês viaja.
 *
 * O `catch` devolve `garantido` em vez de derrubar a rota, e o padrão não é
 * escolha de estilo: endereço com lado malformado é link velho, e o lado que se
 * mostra por omissão é o que a casa DEVE, nunca o que ela perdeu.
 *
 * Fica ao lado do Painel de Gestão neste arquivo porque é o vizinho dele no
 * menu — a ordem daqui acompanha a de `menu.ts`, e uma ordem que discorda da
 * outra é como se registra uma tela no endereço da vizinha sem ninguém notar.
 */
const salesWallet = createRoute({
  getParentRoute: () => authenticated,
  path: '/sales/metrics',
  component: WalletScreen,
  validateSearch: z.object({
    state: z.enum(['garantido', 'perdendo']).catch('garantido'),
    /*
     * De QUEM é a carteira — desde 25/09/2026, com a tabela de todas as
     * carteiras (`sales.management:ver_carteiras`). Ausente é a própria, ou a
     * tabela para quem tem a ação. O mês viaja junto quando se chega pela
     * tabela: abrir a linha de agosto e cair em setembro faria o número da
     * linha e o da carteira discordarem.
     *
     * O número também vale (20/10/2026). O roteador lê a busca como JSON, e
     * `consultant=108` digitado ou colado chega como o NÚMERO 108. Só texto,
     * como era, descartava o código em silêncio e abria a tabela de todas as
     * carteiras no lugar da pessoa. A navegação da tela manda `"108"`, entre
     * aspas, e por isso o defeito só aparecia no link feito à mão.
     */
    consultant: z
      .union([z.string(), z.number().int().nonnegative()])
      .transform(String)
      .pipe(z.string().min(1).max(40))
      .optional()
      .catch(undefined),
    month: z
      .string()
      .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
      .optional()
      .catch(undefined),
  }),
});

/**
 * A CALCULADORA — `/sales/calculator` (29/10/2026).
 *
 * Saiu das Métricas e mora ao lado delas, aqui e no menu. Sem busca: os
 * números são digitados e não são de ninguém — um link com a meta de uma
 * pessoa não ensina nada a quem o abre.
 */
const salesCalculator = createRoute({
  getParentRoute: () => authenticated,
  path: '/sales/calculator',
  component: CalculatorScreen,
});

/**
 * O EXTRATO — `/sales/statement?month=…&search=…&page=…`.
 *
 * ## Tudo viaja na BUSCA, e aqui isso é o oposto da Carteira
 *
 * Lá a competência fica no componente porque a conferência acontece noutra
 * tela. Aqui a conferência **é** esta tela: "setembro, placa ABC1D23" é o
 * endereço que alguém manda para quem atende, e que a pessoa deixa aberto numa
 * aba. Com o mês no estado do componente, o link chegaria no mês de quem
 * abrisse.
 *
 * O `catch` de cada campo devolve o padrão em vez de derrubar a rota: endereço
 * malformado é link velho, e o padrão é o mês corrente, sem busca, na primeira
 * página.
 */
const salesStatement = createRoute({
  getParentRoute: () => authenticated,
  path: '/sales/statement',
  component: StatementScreen,
  validateSearch: z.object({
    month: z
      .string()
      .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
      .optional()
      .catch(undefined),
    search: z.string().max(60).optional().catch(undefined),
    /*
     * O código do voluntário — só quem tem `ver_qualquer` chega aqui com ele.
     * O número também vale, pelo motivo da Carteira: `consultant=117` chega
     * do roteador como NÚMERO, e só texto o descartava em silêncio.
     */
    consultant: z
      .union([z.string(), z.number().int().nonnegative()])
      .transform(String)
      .pipe(z.string().min(1).max(40))
      .optional()
      .catch(undefined),
    page: z.coerce.number().int().min(1).catch(1),
    /* A lista é o padrão, e só o gráfico viaja no endereço. */
    view: z.enum(['grafico']).optional().catch(undefined),
    /*
     * O lote do saldo a destacar (20/10/2026): quem clica numa origem do "Meu
     * saldo", no topo, chega aqui com a competência e a categoria, e a tela
     * abre o saldo em aberto com aquele lote marcado.
     */
    lot: z.enum(['1x', '2x']).optional().catch(undefined),
  }),
});

/*
 * A agenda de clientes — `/sales/portfolio?consultant=…&situation=…`.
 *
 * O recorte viaja na busca pelo motivo do Extrato: "a agenda do consultor 117,
 * só os ativos" é o link que alguém manda para quem atende. O `catch` de cada
 * campo devolve o padrão em vez de derrubar a rota.
 */
const salesAgenda = createRoute({
  getParentRoute: () => authenticated,
  path: '/sales/portfolio',
  component: AgendaScreen,
  validateSearch: z.object({
    /* O código do voluntário — só quem tem `ver_qualquer` chega aqui com ele. */
    consultant: z.string().min(1).max(40).optional().catch(undefined),
    search: z.string().max(60).optional().catch(undefined),
    /*
     * Os recortes de lista levam VÁRIOS valores, separados por vírgula
     * (29/10/2026) — o mesmo texto que a API recebe. Um valor só continua
     * valendo, então o link antigo abre igual.
     */
    situation: z.string().min(1).max(2000).optional().catch(undefined),
    /* Só quem tem carro em tratativa no sinistro (21/10/2026). */
    claim: z.enum(['em_tratativa']).optional().catch(undefined),
    /* Os recortes por carro da Carteira (28/10/2026) — ver a rota da API. */
    vehicleSituation: z.string().min(1).max(500).optional().catch(undefined),
    contractFrom: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional()
      .catch(undefined),
    contractTo: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional()
      .catch(undefined),
    dueDay: z
      .string()
      .regex(/^\d{1,2}(,\d{1,2})*$/)
      .optional()
      .catch(undefined),
    page: z.coerce.number().int().min(1).catch(1),
  }),
});

/*
 * Um cliente da agenda — os veículos dele com o consultor e os boletos.
 * `consultant` viaja junto porque a mesma página serve a agenda própria e a de
 * outra pessoa, e o "Voltar" precisa devolver à agenda de onde se veio.
 *
 * `vehicle` é o carro aberto (o `codigo_veiculo`) e `view` o que se lê dele —
 * os boletos, ou os atendimentos (21/10/2026). Os atendimentos sem `vehicle`
 * são os de todos os carros do cliente com este consultor.
 */
const salesAgendaMember = createRoute({
  getParentRoute: () => authenticated,
  path: '/sales/portfolio/members/$code',
  component: AgendaMemberScreen,
  validateSearch: z.object({
    consultant: z.string().min(1).max(40).optional().catch(undefined),
    vehicle: z.string().min(1).max(40).optional().catch(undefined),
    view: z.enum(['boletos', 'atendimentos']).optional().catch(undefined),
  }),
});

/**
 * O relatório de uma premiação — `/sales/management/portfolio?award=…&month=…`.
 *
 * ## Por que a premiação e a competência viajam na BUSCA, e não no caminho
 *
 * A tela responde "o que gerou este valor", e o valor só existe dentro de uma
 * competência: `/sales/management/portfolio/12` seria um endereço que não
 * identifica nada sozinho. Na busca, o link copiado do navegador leva quem o
 * receber para a mesma lista — que é o uso real desta tela, conferir um número
 * com outra pessoa.
 *
 * O `catch` de `month` devolve a competência corrente em vez de derrubar a
 * rota: endereço com mês malformado é link velho, não erro de programa.
 */
const salesAwardPortfolio = createRoute({
  getParentRoute: () => authenticated,
  path: '/sales/management/portfolio',
  component: AwardPortfolioScreen,
  validateSearch: z.object({
    award: z.coerce.number().int(),
    month: z
      .string()
      .regex(/^\d{4}-\d{2}$/)
      .catch(() => new Date().toISOString().slice(0, 7)),
    /**
     * De quem é a carteira. Ausente é "a minha", que é o que a rota da API lê
     * quando o parâmetro não vai — e é o que estava certo enquanto a única
     * porta para cá era o próprio painel.
     */
    consultant: z.string().min(1).max(40).optional(),
    /**
     * De que tela se veio, para a volta levar a ela. `carteira` desde
     * 25/09/2026: quem tem `ver_carteiras` sem `ver_qualquer` e abre a lista
     * de outra pessoa pela Carteira voltaria ao Painel dela — e o Painel pede
     * `ver_qualquer`.
     *
     * Desde 20/10/2026 a tela não o lê: as premiações moram só no painel, e
     * toda volta é para ele. Fica aceito para o link salvo não quebrar.
     *
     * `inicio` desde 28/10/2026: quem chega pelo Início do Comercial volta
     * para ele, e só ele vê o gráfico do dia a dia acima do relatório.
     */
    origin: z.enum(['carteira', 'inicio']).optional().catch(undefined),
    /**
     * De que LADO da competência é a lista, e ele precisa ser o mesmo da tela de
     * origem: uma lista de boletos pagos sob um total de boletos em aberto não
     * confere nada. Ausente é `garantido`, que é o padrão do contrato.
     */
    state: z.enum(['garantido', 'perdendo']).catch('garantido'),
  }),
});

const sales = createRoute({
  getParentRoute: () => authenticated,
  path: '/sales/sales',
  component: SalesScreen,
});

const targets = createRoute({
  getParentRoute: () => authenticated,
  path: '/sales/targets',
  component: TargetsScreen,
});

const ranking = createRoute({
  getParentRoute: () => authenticated,
  path: '/sales/ranking',
  component: RankingScreen,
});

const commissions = createRoute({
  getParentRoute: () => authenticated,
  path: '/sales/commissions',
  component: CommissionsScreen,
});

/*
 * O Relatório de Vendas — `/sales/reports?tab=…&month=…&consultant=…&team=…&days=…`.
 *
 * O recorte viaja no endereço pelo motivo do Extrato: "setembro, o time
 * Meteoro, a segunda semana" é o link que um líder manda para outro. Semana e
 * dia viajam separados, como a pessoa os escolheu; quem os junta num conjunto
 * de dias para a API é a tela. O `catch` de cada campo devolve o padrão em vez
 * de derrubar a rota.
 */
const salesReports = createRoute({
  getParentRoute: () => authenticated,
  path: '/sales/reports',
  component: SalesReportScreen,
  validateSearch: z.object({
    /* O painel é o padrão, e só a comparação viaja no endereço. */
    tab: z.enum(['comparacao']).optional().catch(undefined),
    /* Os dois meses da comparação; ausentes, o anterior e o corrente. */
    baseline: z
      .string()
      .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
      .optional()
      .catch(undefined),
    reference: z
      .string()
      .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
      .optional()
      .catch(undefined),
    /* A sessão analisada na comparação; ausente é Vendas. */
    session: z
      .enum(['geral', 'troca', 'reativacao', 'bronze'])
      .optional()
      .catch(undefined),
    month: z
      .string()
      .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
      .optional()
      .catch(undefined),
    consultant: z.coerce.number().int().positive().optional().catch(undefined),
    team: z.coerce.number().int().positive().optional().catch(undefined),
    /*
     * A semana do mês (de segunda a domingo) e os dias, como LISTA de números:
     * `?weeks=[2,3]&days=[7]`.
     *
     * O roteador lê a busca como JSON. Com texto `1,2` o `?weeks=2` chegava como o
     * número 2, um `string()` o recusava e o `catch` o apagava — o link de "a
     * segunda semana" abria o mês inteiro sem dizer nada; com `coerce`, voltava
     * regravado como `"2"`. A lista tem uma forma só, na ida e na volta.
     */
    weeks: z.array(z.number().int().min(1).max(6)).min(1).optional().catch(undefined),
    days: z.array(z.number().int().min(1).max(31)).min(1).optional().catch(undefined),
  }),
});

/**
 * O painel do time — `/sales/team`.
 *
 * ## Tela própria, e não uma aba do ranking
 *
 * O recurso é outro (`sales.team_performance`), e é a regra deste bloco: a
 * fronteira da tela acompanha a fronteira da permissão, nunca a do assunto. Uma
 * aba dentro do ranking daria a um líder a primeira aba devolvendo 403 — ele
 * não precisa do quadro da praça para conferir a própria equipe, e é justamente
 * por isso que o recurso nasceu separado.
 *
 * ## O time NÃO vai no caminho
 *
 * `/sales/team/5` seria um endereço que promete um time por número, e a
 * resposta certa para quem não lidera o 5 é 403 — que é uma página de erro onde
 * deveria haver a equipe da pessoa. O time viaja na consulta e é opcional:
 * ausente, o servidor responde o que quem pede lidera. Quem tem a ação larga
 * troca pelo seletor, e a escolha não fica no endereço pelo mesmo motivo que
 * a competência não fica: nenhuma das duas identifica a página sozinha.
 */
const teamPanel = createRoute({
  getParentRoute: () => authenticated,
  path: '/sales/team',
  component: TeamPanelScreen,
});

/* Os modelos de premiação — a régua cadastrada uma vez, vinculada aos consultores. */
const awardTemplates = createRoute({
  getParentRoute: () => authenticated,
  path: '/sales/award-templates',
  component: AwardTemplatesScreen,
});

/*
 * O livro-caixa. O componente chama-se `CashBookScreen` e o endereço é
 * `/sales/finance` — o endereço segue o recurso que o guard exige
 * (`sales.finance`), não o nome do arquivo. Quem procurar a tela pelo
 * menu chega por "Livro-caixa"; quem procurar pela permissão chega pelo
 * financeiro, e os dois precisam bater.
 */
const cashBook = createRoute({
  getParentRoute: () => authenticated,
  path: '/sales/finance',
  component: CashBookScreen,
});

/*
 * Consultores, times e hierarquia — uma tela só, `ConsultantsScreen`, no endereço que
 * o plano do módulo nomeia, com uma aba para cada (`?tab=teams|hierarchy`). É a tela que vincula usuário ao código do ERP, e o
 * código é POR BASE: o mesmo operador é 394 no CE e 75 no GO.
 */
const salesRecord = createRoute({
  getParentRoute: () => authenticated,
  path: '/sales/consultants',
  component: ConsultantsScreen,
  validateSearch: consultantsSearch,
});

/*
 * A régua de comissão, sob `sales.tiers` — recurso separado pela mesma
 * lição que a cobrança escreveu: quem opera a mesa não muda a régua da casa.
 * Aqui a lição é mais cara, porque a régua é o número que multiplica a placa de
 * gente nomeada.
 *
 * O endereço é `/sales/tiers`, igual ao `/billing/tiers` do outro módulo
 * — convenção 7, um nome por conceito, com o prefixo separando os dois. O
 * componente é que não repete: `LadderScreen` e `TiersScreen` são importados neste
 * mesmo arquivo, e dois nomes iguais pediriam um apelido de import, que é como
 * a tela errada acaba montada num endereço sem ninguém notar.
 */
const commissionLadder = createRoute({
  getParentRoute: () => authenticated,
  path: '/sales/tiers',
  component: LadderScreen,
});

/*
 * O acesso do associado, administrado de dentro. Fica sob `/portal/*` no
 * endereço da INTERNA — que é outra origem do portal do associado, e não se
 * confunde com ele: `localhost:5273/portal/members` é a operação olhando;
 * `localhost:5274` é o associado.
 */
const members = createRoute({
  getParentRoute: () => authenticated,
  path: '/portal/members',
  component: MembersScreen,
});

const member = createRoute({
  getParentRoute: () => authenticated,
  path: '/portal/members/$id',
  component: MemberScreen,
});

const tree = root.addChildren([
  signIn,
  firstAccess,
  forgotPassword,
  resetPassword,
  authenticated.addChildren([
    start,
    notFound,
    users,
    groups,
    departments,
    sessions,
    password,
    preferences,
    audit,
    tenantSetting,
    usefulLinks,
    breakRecords,
    breakOverruns,
    breakSettings,
    assistanceProviders,
    assistanceDispatches,
    legacyAssistanceNew,
    assistanceAgenda,
    legacyAssistanceDrafts,
    assistanceSettlements,
    assistanceExpenses,
    assistanceManagement,
    integrations,
    contactBlock,
    assistanceQuotas,
    assistanceDeadlines,
    assistanceServicesRoute,
    beneficiaries,
    tickets,
    ticketReport,
    ticketBoard,
    ticketCategories,
    ticket,
    erpHealth,
    erpVehicles,
    erpVehicle,
    erpPlans,
    erpBoletos,
    erpBoleto,
    erpProducts,
    erpProductVehicles,
    erpServiceRecords,
    erpServiceRecord,
    erpChanges,
    erpChange,
    erpBoletoTypes,
    erpMembers,
    erpMember,
    erpVolunteers,
    statusesClassification,
    claimsList,
    claimsDetail,
    claimsAnalysisBoard,
    claimsRepairBoard,
    claimsRepairSupervisionBoard,
    claimsCompensationBoard,
    claimsThirdParties,
    claimsThirdParty,
    companiesRoute,
    institutions,
    legacyClaimsProviders,
    legacyClaimsInstitutions,
    claimsPeripherals,
    claimsClosures,
    claimsExpenses,
    claimsManagement,
    claimsTeamMap,
    claimsPeripheralManagement,
    claimsPhaseDeadlines,
    claimsRotations,
    recurrencePayments,
    myRecurrencePayments,
    financeRevenues,
    financePaymentAccounts,
    financeChargesRedirect,
    financeExpenses,
    financeDashboard,
    financeTeamMap,
    saleExpenses,
    mySaleExpenses,
    retentionBoard,
    retentionOperators,
    retentionLabels,
    retentionReport,
    retentionRequest,
    billingDesk,
    billingDeskImport,
    billingDeskSettings,
    billingDeskDashboard,
    billingDeskMoves,
    billingAwards,
    billingAwardsLinks,
    billingAwardsData,
    billingAwardsLog,
    billingAwardsSettings,
    billingAwardsOperator,
    billingAwardsHistory,
    billingPipeline,
    billingLadder,
    salesHome,
    salesManagementPanel,
    salesWallet,
    salesCalculator,
    salesStatement,
    salesAgenda,
    salesAgendaMember,
    salesConsultantPanel,
    salesOtherConsultantPanel,
    salesAwardPortfolio,
    sales,
    targets,
    ranking,
    salesReports,
    teamPanel,
    awardTemplates,
    commissions,
    cashBook,
    salesRecord,
    commissionLadder,
    members,
    member,
  ]),
]);

export function buildRouter(client: QueryClient) {
  return createRouter({ routeTree: tree, context: { client } });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof buildRouter>;
  }
}
